import { DateInput, FormField, UpgradePrompt, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { todayInTunis } from '@/features/admin/fiscal/model';
import { useNotify } from '@/shared/hooks/use-notify';
import { isStepUpRequired } from '../model';
import { useCreateInvitation } from './api';
import {
  withoutKey,
  EMPTY_LIMITED_DRAFT,
  INITIAL_PERSON_VALUES,
  RIGHTS_ORDER,
  buildPersonInvite,
  errorCodeOf,
  isRights,
  sharingErrorMessage,
  type PersonErrors,
  type PersonKind,
  type PersonValues,
} from './model';
import { ScopePicker } from './scope-picker';

const useStyles = __css({"form":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"intro":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42"}});

const LTR = 'ltr';
const NOMINATIVE: PersonKind = 'nominative';
const GUEST: PersonKind = 'guest';

const isKind = (value: string): value is PersonKind => value === 'nominative' || value === 'guest';

export interface InvitePersonDialogProps {
  spaceId: string;
  open: boolean;
  onClose: () => void;
}

type Problem = { kind: 'guestLimit' } | { kind: 'other'; message: string };

function InvitePersonForm({ spaceId, onClose }: Omit<InvitePersonDialogProps, 'open'>) {
  const { t } = useTranslation('spaces');
  const { t: tCommon } = useTranslation('common');
  const styles = useStyles();
  const notify = useNotify();
  const locale = useLocale();
  const create = useCreateInvitation(spaceId);

  const [values, setValues] = useState<PersonValues>(INITIAL_PERSON_VALUES);
  const [errors, setErrors] = useState<PersonErrors>({});
  const [problem, setProblem] = useState<Problem | null>(null);
  const guest = values.kind === 'guest';

  const change = (patch: Partial<PersonValues>) => {
    setValues((current) => ({ ...current, ...patch }));
  };

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (create.isPending) return;
    setProblem(null);
    const result = buildPersonInvite(values, t, new Date());
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    try {
      const invitation = await create.mutateAsync({
        spaceId,
        data: { ...result.data, locale },
      });
      notify({
        intent: 'success',
        message: t(
          invitation.status === 'pending_approval'
            ? 'sharing.person.sentWaitingApproval'
            : 'sharing.person.sent',
          { email: isolate(result.data.email) },
        ),
      });
      onClose();
    } catch (error) {
      if (isStepUpRequired(error)) return;
      setProblem(
        errorCodeOf(error) === 'PLAN_LIMIT_REACHED'
          ? { kind: 'guestLimit' }
          : {
              kind: 'other',
              message: sharingErrorMessage(t, tCommon, error, {
                forbidden: 'sharing.person.errors.forbidden',
              }),
            },
      );
    }
  };

  return (
    <form
      noValidate
      aria-label={t('sharing.person.title')}
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <DialogBody>
        <DialogTitle>{t('sharing.person.title')}</DialogTitle>
        <DialogContent>
          <div className={styles.form}>
            <p className={styles.intro}>{t('sharing.person.intro')}</p>
            <FormField
              required
              label={t('sharing.person.email.label')}
              hint={t('sharing.person.email.hint')}
              error={errors.email}
            >
              <Input
                type="email"
                autoComplete="off"
                dir={LTR}
                value={values.email}
                onChange={(_event, data) => {
                  change({ email: data.value });
                  setErrors((current) => withoutKey(current, 'email'));
                }}
              />
            </FormField>

            <FormField
              label={t('sharing.person.kind.label')}
              hint={t(`sharing.person.kind.${values.kind}Hint`)}
            >
              <RadioGroup
                value={values.kind}
                onChange={(_event, data) => {
                  if (!isKind(data.value)) return;
                  // A guest never gets the whole space: start again from an empty choice.
                  change({
                    kind: data.value,
                    ...(data.value === 'guest' && values.scope.whole
                      ? { scope: EMPTY_LIMITED_DRAFT }
                      : {}),
                  });
                  setProblem(null);
                }}
              >
                <Radio value={NOMINATIVE} label={t('sharing.person.kind.nominative')} />
                <Radio value={GUEST} label={t('sharing.person.kind.guest')} />
              </RadioGroup>
            </FormField>

            <ScopePicker
              spaceId={spaceId}
              allowWhole={!guest}
              value={guest ? { ...values.scope, whole: false } : values.scope}
              error={errors.scope ?? null}
              onChange={(scope) => {
                change({ scope });
                setErrors((current) => withoutKey(current, 'scope'));
              }}
            />

            <FormField label={t('sharing.rightsOption.label')}>
              <RadioGroup
                value={values.rights}
                onChange={(_event, data) => {
                  if (isRights(data.value)) change({ rights: data.value });
                }}
              >
                {RIGHTS_ORDER.map((rights) => (
                  <Radio key={rights} value={rights} label={t(`sharing.rightsOption.${rights}`)} />
                ))}
              </RadioGroup>
            </FormField>

            {guest ? (
              <MessageBar intent="info" layout="multiline">
                <MessageBarBody>{t('sharing.person.guestDuration')}</MessageBarBody>
              </MessageBar>
            ) : (
              <DateInput
                label={t('sharing.share.end.label')}
                hint={t('sharing.person.end.hint')}
                value={values.endsOn}
                min={todayInTunis(new Date())}
                error={errors.endsOn}
                onChange={(endsOn) => {
                  change({ endsOn });
                  setErrors((current) => withoutKey(current, 'endsOn'));
                }}
              />
            )}

            {problem?.kind === 'guestLimit' ? (
              <UpgradePrompt message={t('sharing.person.errors.guestLimit')} />
            ) : null}
            {problem?.kind === 'other' ? (
              <MessageBar intent="error" layout="multiline">
                <MessageBarBody>{problem.message}</MessageBarBody>
              </MessageBar>
            ) : null}
          </div>
        </DialogContent>
        <DialogActions>
          <Button type="button" appearance="secondary" onClick={onClose}>
            {tCommon('actions.cancel')}
          </Button>
          <Button type="submit" appearance="primary" disabledFocusable={create.isPending}>
            {create.isPending ? t('sharing.person.submitting') : t('sharing.person.submit')}
          </Button>
        </DialogActions>
      </DialogBody>
    </form>
  );
}

/**
 * "Inviter une personne": a named person (any scope, optional end date; their organisation may
 * have to approve) or a guest (folders and channels only, 90 days, extendable).
 */
export function InvitePersonDialog({ open, onClose, ...rest }: InvitePersonDialogProps) {
  return (
    <Dialog
      open={open}
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <InvitePersonForm {...rest} onClose={onClose} />
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/sharing/invite-person-dialog.griffel.css";